import { LocalDate } from "@flockcover/flock-svc-core-types"
import { navigate } from "@reach/router"
import { Box, DatePicker, Flex, useFormValidators } from "@shared/primitive-components"
import { useReducer } from "react"
import { AddedFleetEntry } from "../../../../api/fleet"
import { ActionButtons } from "../../../../components/ActionButtons"
import { HAVEN_ID } from "../../../../const/policies"
import { useAccess, usePolicyAndFleet } from "../../../../contexts"
import { useFleetEntryValidation } from "../../../validatation/useFleetEntryValidation"
import { VRNInput } from "./VRNInput"
import { addFleetEntryFormReducer, makeInitialState } from "./addFleetEntryFormReducer"
import { getInitalRows } from "./addFleetEntryFormUtils"
import * as styles from "./styles/formStyles"

export type AddFleetEntryFormProps = {
  initialValue?: ReadonlyArray<AddedFleetEntry>
  dataTestId?: string
  onValidEntriesAdded: (entries: ReadonlyArray<AddedFleetEntry>) => void
}

export const ADD_FLEET_ENTRY_FORM_DTI = {
  FIELDS: {
    VRN: `add_fleet_entry_form_vrn_field`,
    ON_COVER: `add_fleet_entry_form_on_cover_field`,
    OFF_COVER: `add_fleet_entry_form_off_cover_field`,
  },
  BUTTONS: {
    NEXT: `add_fleet_entry_form_next_button`,
  },
}

export const AddFleetEntryForm = ({ initialValue, dataTestId, onValidEntriesAdded }: AddFleetEntryFormProps) => {
  const { policy, fleet } = usePolicyAndFleet()
  const isHavenPolicy = policy.underwriter.id === HAVEN_ID
  const isCourier = policy.meta?.segment === `courier`
  const hasAdminAccess = useAccess().hasAdminAccess()

  const [state, dispatch] = useReducer(addFleetEntryFormReducer, makeInitialState(getInitalRows(initialValue)))

  const { validateRows } = useFleetEntryValidation(
    fleet.data || [],
    { startTime: policy.startTime, endTime: policy.endTime },
    hasAdminAccess,
    isHavenPolicy,
    isCourier,
    {
      onCoverDateRequired: true,
    }
  )

  const { required } = useFormValidators()

  const saveChanges = () => {
    const rows = state.rows
      .filter((row) => row.registrationNumber?.length > 0)
      .map((row) => ({ ...row, registrationNumber: row.registrationNumber.trim() }))
    if (!rows.length) return
    const errors = validateRows(rows)
    dispatch({ type: `setErrors`, errors })
    const hasError = errors.some((it) => Object.values(it).some((maybeError) => !!maybeError))
    if (hasError) return
    onValidEntriesAdded(rows)
  }

  return (
    <Flex flexDirection="column" width="100vw" data-testid={dataTestId}>
      <Flex css={styles.headingsWrapper}>
        <Flex css={styles.makeHeadingColumnStyle()}>VRN</Flex>
        <Flex css={styles.makeHeadingColumnStyle(10)}>Cover On Date</Flex>
        <Flex css={styles.makeHeadingColumnStyle(4)}>Cover Off Date</Flex>
      </Flex>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          saveChanges()
        }}
      >
        {state.rows.map((row, index) => (
          <Flex css={styles.rowWrap} key={index}>
            <Box css={styles.makeCellWrapStyle()}>
              <VRNInput
                value={row.registrationNumber}
                onChange={(value: string) => {
                  dispatch({ type: `setVrn`, data: { index, value } })
                }}
                error={state.errors[index]?.registrationNumber}
                dataTestId={`${ADD_FLEET_ENTRY_FORM_DTI.FIELDS.VRN}_${index}`}
              />
            </Box>
            <Flex css={styles.makeCellWrapStyle()}>
              <DatePicker
                value={row.onCoverDate}
                onChange={(value?: LocalDate) => {
                  dispatch({ type: `setOnCoverDate`, data: { index, value } })
                }}
                name="onCoverDate"
                validate={required()}
                variant="form"
                error={state.errors[index]?.onCoverDate}
                dataTestId={`${ADD_FLEET_ENTRY_FORM_DTI.FIELDS.ON_COVER}_${index}`}
              />
            </Flex>
            <Flex css={styles.makeCellWrapStyle(true)}>
              <DatePicker
                value={row.offCoverDate || undefined}
                onChange={(value?: LocalDate) => {
                  dispatch({ type: `setOffCoverDate`, data: { index, value } })
                }}
                name="offCoverDate"
                variant="form"
                error={state.errors[index]?.offCoverDate}
                dataTestId={`${ADD_FLEET_ENTRY_FORM_DTI.FIELDS.OFF_COVER}_${index}`}
              />
            </Flex>
          </Flex>
        ))}
        <ActionButtons
          onCancel={() => navigate(`fleet`)}
          dataTestIds={{ next: ADD_FLEET_ENTRY_FORM_DTI.BUTTONS.NEXT }}
        />
      </form>
    </Flex>
  )
}

AddFleetEntryForm.defaultProps = {
  initialValue: undefined,
  dataTestId: undefined,
}
